
import { useRef, useEffect, useCallback } from "react";
import { useNews } from "@/context/NewsContext";
import ArticleCard from "@/components/ui/ArticleCard";
import { ArticleCardSkeleton } from "@/components/ui/skeletons";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useT } from "@/i18n/useLocale";
import T from "@/i18n/T";

const NewsList = () => {
  const { articles, isLoading, error, pagination, setFilter, allCachedArticles } = useNews();
  const t = useT();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement | null>(null);

  // Load more articles by increasing page
  const loadMoreArticles = useCallback(() => {
    if (!isLoading && pagination.page < pagination.totalPages) {
      setFilter("page", pagination.page + 1);
    }
  }, [isLoading, pagination.page, pagination.totalPages, setFilter]);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (loadingRef.current && pagination.page < pagination.totalPages) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreArticles();
          }
        },
        { 
          threshold: 0.1,
          rootMargin: '100px' // Start loading 100px before reaching the trigger
        }
      );

      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreArticles, pagination.page, pagination.totalPages]);

  // Error state
  if (error && articles.length === 0) {
    return (
      <div className="text-center py-16 w-full">
        <h3 className="text-xl font-semibold mb-2 text-destructive">{t("feed.failed")}</h3>
        <p className="text-muted-foreground mb-4">{error.message || 'An error occurred while fetching articles'}</p>
        <Button onClick={() => window.location.reload()}>{t("feed.reload")}</Button>
      </div>
    );
  }

  // Initial loading state
  if (isLoading && articles.length === 0 && allCachedArticles.length === 0) {
    return (
      <div className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(12)].map((_, i) => (
            <ArticleCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // No articles state
  if (!isLoading && articles.length === 0 && allCachedArticles.length === 0) {
    return (
      <div className="text-center py-16 w-full">
        <h3 className="text-xl font-semibold mb-2">{t("feed.noArticles")}</h3>
        <p className="text-muted-foreground mb-4">{t("feed.emptyHint")}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t("feed.refresh")}
        </Button>
      </div>
    );
  }

  // No filtered results state
  if (articles.length === 0 && allCachedArticles.length > 0) {
    return (
      <div className="text-center py-16 w-full">
        <h3 className="text-xl font-semibold mb-2">{t("feed.noMatch")}</h3>
        <p className="text-muted-foreground">{t("feed.noMatchHint")}</p>
      </div>
    );
  }

  const hasMoreToLoad = pagination.page < pagination.totalPages;
  const showingCount = articles.length;

  return (
    <div className="space-y-4 md:space-y-6 w-full">
      {/* Article count indicator */}
      <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
        <span>{t("feed.showing").replace("{count}", String(showingCount))}</span>
        {hasMoreToLoad && <span>{t("feed.scrollForMore")}</span>}
      </div>


      {/* Articles grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 w-full">
        {articles
          .filter(article => !article.content?.includes("Continue reading the full article"))
          .map((article, index) => (
            <ArticleCard key={article.id} article={article} variant="grid" priority={index < 4} />
        ))}
      </div>

      {/* Loading more articles */}
      {isLoading && articles.length > 0 && (
        <div className="py-6 md:py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {[...Array(4)].map((_, i) => (
              <ArticleCardSkeleton key={i} />
            ))}
          </div>
        </div>
      )}
      
      {/* Infinite scroll trigger - invisible element */}
      {hasMoreToLoad && !isLoading && (
        <div 
          ref={loadingRef} 
          className="h-10 w-full flex items-center justify-center"
        >
          <div className="text-sm text-muted-foreground">
            {t("feed.loadingMore")}
          </div>
        </div>
      )}

      {/* Load more button as fallback */}
      {hasMoreToLoad && !isLoading && (
        <div className="flex justify-center py-6">
          <Button
            onClick={loadMoreArticles}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Loader2 className="h-4 w-4" />
            <T k="auto.load-more-articles-eb53" />
          </Button>
        </div>
      )}

      {/* End of articles indicator */}
      {!hasMoreToLoad && articles.length > 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          <div className="border-t border-border/50 pt-4">
            You've reached the end of all articles ({showingCount} total)
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsList;
